// =============================================================================
// TrackerModal.jsx
//
// Modal de criar / editar uma entrada do Tracker.
//
// Contém 4 componentes internos + o modal principal (exportado):
//   SectionLabel → separador visual de secção
//   PhaseCard    → card editável de uma fase (Phase 1, Rollback…)
//   ServerCard   → cascade OMC_ID → TAC → Sites
//   TrackerModal → modal principal
//
// O SiteListDialog vive no TrackerPage (não aqui) para evitar o bug do MUI
// em que o fecho de um Dialog dispara o backdrop do outro.
//
// Mudanças para MUI v6 (@mui/material ^6.4.1):
//   → Grid: usa Grid2 (import de @mui/material/Grid2) com prop size={}
//     em vez de <Grid item xs={} sm={}>
//   → TextField: InputProps → slotProps.input
//                InputLabelProps → slotProps.inputLabel
// =============================================================================

import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Typography,
  IconButton, Box, Divider, CircularProgress,
  Chip, Tooltip, LinearProgress, InputAdornment, Checkbox, ListSubheader,
} from '@mui/material';
import AddIcon           from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon         from '@mui/icons-material/Close';
import ListAltIcon       from '@mui/icons-material/ListAlt';
import DownloadIcon      from '@mui/icons-material/Download';
import {
  apiGetServers,
  apiGetTechnologies,
  apiGetVendors,
  apiGetFeatures,
  apiGetTeams,
  apiGetOwnersByTeam,
  apiExportById,
} from '../requests/trackerRequests';


// =============================================================================
// UTILITÁRIOS
// =============================================================================

// ID temporário para phases/servers criados no browser.
// Prefixo "tmp_" torna claro que ainda não existe na BD.
const uid = () => `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const makePhase  = (label = 'Phase 1') => ({ id: uid(), label, date: '', crq: '', comments: '' });
const makeServer = ()                   => ({ id: uid(), omcId: '', tac: '', allSites: [], selectedSites: [] });

// Garante que um server da API tem sempre allSites e selectedSites como arrays
const normalizeServer = (s) => ({
  ...makeServer(),
  ...s,
  allSites:      Array.isArray(s.allSites)      ? s.allSites      : [],
  selectedSites: Array.isArray(s.selectedSites) ? s.selectedSites : [],
});

// Normaliza datas da API para "yyyy-MM-dd" que o input[type="date"] exige.
//
// Formatos confirmados da API:
//   "02/22/2026 00:00:00" → MM/dd/yyyy (SQL Server cultura en-US com barras)
//   "2025-02-24"          → yyyy-MM-dd (ISO correcto — já está bem)
//   "2026-22-02T00:00:00" → yyyy-dd-MM (dia e mês trocados — detectado pela regra >12)
//
// Regra de desambiguação para hífens (yyyy-A-B):
//   Se A > 12 → A é o dia, B é o mês → trocar
//   Caso contrário → já está em yyyy-MM-dd
const toDateInput = (raw) => {
  if (!raw) return '';
  const s = String(raw);

  // Formato com barras: MM/dd/yyyy HH:mm:ss  (formato confirmado da API)
  const slashMatch = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slashMatch) {
    const [, mm, dd, yyyy] = slashMatch;
    return `${yyyy}-${mm}-${dd}`;
  }

  // Formato com hífens: yyyy-A-B (ISO ou yyyy-dd-MM)
  const dashMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dashMatch) {
    const [, yyyy, a, b] = dashMatch;
    // Se A > 12 → está em yyyy-dd-MM → trocar
    return Number(a) > 12
      ? `${yyyy}-${b}-${a}`
      : `${yyyy}-${a}-${b}`;
  }

  // Fallback genérico via Date
  const d = new Date(s);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const emptyForm = () => ({
  title: '', technology: '', vendor: '', feature: '',
  startDate: '', endDate: '',
  teamId: '', teamName: '', ownerIds: [], ownerNames: [],
  servers:  [],
  phases:   [],
  comments: '',
});


// =============================================================================
// SectionLabel — separador visual de secção
// =============================================================================
const SectionLabel = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 0.5 }}>
    <Typography variant="overline" sx={{
      color: 'text.disabled', fontSize: '0.63rem',
      fontWeight: 700, letterSpacing: 1.5, lineHeight: 1, whiteSpace: 'nowrap',
    }}>
      {children}
    </Typography>
    <Divider sx={{ flex: 1 }} />
  </Box>
);


// =============================================================================
// PhaseCard — card editável de uma fase
//
// Props:
//   phase    → { id, label, date, crq, comments }
//   onChange → callback(phaseActualizada)
//   onRemove → callback()
// =============================================================================
const PhaseCard = ({ phase, onChange, onRemove }) => {
  const isRollback = phase.label?.startsWith('Rollback');

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: isRollback ? 'error.dark' : 'divider',
      borderRadius: 2, p: 2,
      bgcolor: isRollback ? 'rgba(244,67,54,0.04)' : 'action.hover',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <TextField
          size="small"
          value={phase.label}
          onChange={(e) => onChange({ ...phase, label: e.target.value })}
          placeholder="Phase name"
          sx={{
            width: 150,
            '& input': { fontWeight: 700, fontSize: '0.8rem', color: isRollback ? 'error.main' : 'inherit' },
          }}
        />
        <IconButton size="small" onClick={onRemove} color="error">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* MUI v6: Grid2 com size= em vez de item xs= sm= */}
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            fullWidth size="small" type="date" label="Date"
            value={phase.date}
            onChange={(e) => onChange({ ...phase, date: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <TextField
            fullWidth size="small" label="CRQ"
            value={phase.crq}
            onChange={(e) => onChange({ ...phase, crq: e.target.value })}
            placeholder="CRQ-YYYY-XXXX"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth size="small" label="Comments"
            value={phase.comments}
            onChange={(e) => onChange({ ...phase, comments: e.target.value })}
            placeholder="Notes for this phase…"
          />
        </Grid>
      </Grid>
    </Box>
  );
};


// =============================================================================
// ServerCard — cascade OMC_ID → TAC → botão Sites
//
// Props:
//   server         → { id, omcId, tac, allSites, selectedSites }
//   technology     → string — tecnologia do formulário pai
//   onChange       → callback(serverActualizado)
//   onRemove       → callback()
//   onOpenSiteList → callback(server) — avisa o TrackerPage para abrir o dialog
// =============================================================================
const ServerCard = ({ server, technology, vendor, onChange, onRemove, onOpenSiteList }) => {
  const [serverRows, setServerRows] = useState([]);
  const [omcOptions, setOmcOptions] = useState([]);
  const [tacOptions, setTacOptions] = useState([]);
  // Inicializa como true se já há tecnologia — evita o micro-render onde
  // loading=false mas omcOptions=[] ainda, que causa o warning out-of-range
  const [loading,    setLoading]    = useState(Boolean(technology));
  const [loadError,  setLoadError]  = useState(null);

  // Carrega servidores sempre que a tecnologia ou o vendor mudam
  useEffect(() => {
    if (!technology || !vendor) return;
    setLoading(true);
    setLoadError(null);

    apiGetServers(technology, vendor)
      .then((res) => {
        const rows = res.data ?? [];
        setServerRows(rows);

        // Constrói omcOptions a partir dos dados da API.
        // Se o omcId vem vazio do backend (bug do servidor), garante que pelo menos
        // o valor já guardado na entry aparece como opção válida — evita o warning
        // de out-of-range do MUI e permite ao utilizador ver o valor actual.
        const omcFromApi    = [...new Set(rows.map((r) => r.omcId).filter(Boolean))];
        const omcWithSaved  = server.omcId && !omcFromApi.includes(server.omcId)
          ? [...omcFromApi, server.omcId]
          : omcFromApi;
        setOmcOptions(omcWithSaved.sort());

        // Restaura tacOptions para o omcId já seleccionado (modo edição)
        if (server.omcId) {
          const tacFromApi   = [...new Set(rows.filter((r) => r.omcId === server.omcId).map((r) => r.tac))];
          const tacWithSaved = server.tac && !tacFromApi.includes(server.tac)
            ? [...tacFromApi, server.tac]
            : tacFromApi;
          setTacOptions(tacWithSaved.sort());
        }

        // Reconstrói allSites em modo edição.
        // O backend só persiste selectedSites — allSites é estado local que se
        // perde quando o modal fecha. Ao reabrir, reconstruímos allSites a partir
        // das rows do GetServers para que o botão "Site List" reapareça.
        if (server.omcId && server.tac) {
          const rebuilt = rows
            .filter((r) => r.omcId === server.omcId && r.tac === server.tac)
            .map((r) => r.csr)
            .sort();
          if (rebuilt.length > 0) {
            onChange({ ...server, allSites: rebuilt });
          }
        }
      })
      .catch(() => setLoadError('Failed to load servers.'))
      .finally(() => setLoading(false));
  }, [technology, vendor]);

  // OMC_ID seleccionado → calcula TACs disponíveis e limpa TAC/sites
  const handleOmcChange = (omcId) => {
    setTacOptions(
      [...new Set(serverRows.filter((r) => r.omcId === omcId).map((r) => r.tac))].sort()
    );
    onChange({ ...server, omcId, tac: '', allSites: [], selectedSites: [] });
  };

  // TAC seleccionado → extrai sites, selecciona todos por defeito (opt-out)
  const handleTacChange = (tac) => {
    const sites = serverRows
      .filter((r) => r.omcId === server.omcId && r.tac === tac)
      .map((r) => r.csr)
      .sort();
    onChange({ ...server, tac, allSites: sites, selectedSites: [...sites] });
  };

  const allSites      = server.allSites      ?? [];
  const selectedSites = server.selectedSites ?? [];
  const hasSites      = allSites.length > 0;
  const hasExclusions = selectedSites.length < allSites.length;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: 'action.hover' }}>
      {loading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>

        {/* OMC_ID
            Enquanto loading=true os omcOptions ainda estão vazios.
            Passar o valor real (ex: "ER-ENM-C1") nessa altura faz o MUI lançar
            o warning "out-of-range" porque o valor não existe nas opções ainda.
            Solução: value="" durante o loading — após o fetch o valor é restaurado
            automaticamente porque o omcOptions já contém a opção correcta. */}
        <TextField
          select size="small" label="Server (OMC_ID)"
          value={loading ? '' : (server.omcId ?? '')}
          onChange={(e) => handleOmcChange(e.target.value)}
          sx={{ minWidth: 220 }}
          disabled={loading}
        >
          {omcOptions.length === 0
            ? <MenuItem value="" disabled>{loading ? 'Loading…' : 'No servers available'}</MenuItem>
            : omcOptions.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)
          }
        </TextField>

        {/* TAC — mesma lógica: value="" enquanto loading ou tacOptions ainda vazio */}
        <TextField
          select size="small" label="TAC"
          value={(loading || tacOptions.length === 0) ? '' : (server.tac ?? '')}
          onChange={(e) => handleTacChange(e.target.value)}
          sx={{ minWidth: 160 }}
          disabled={!server.omcId || loading || tacOptions.length === 0}
        >
          {tacOptions.map((o) => <MenuItem key={o} value={String(o)}>{o}</MenuItem>)}
        </TextField>

        {/* Botão Sites — só visível depois de seleccionar TAC */}
        {hasSites && (
          <Tooltip title={
            hasExclusions
              ? `${selectedSites.length} of ${allSites.length} sites selected`
              : 'All sites selected'
          }>
            <Button
              size="small" variant="outlined"
              startIcon={<ListAltIcon />}
              color={hasExclusions ? 'warning' : 'primary'}
              onClick={() => onOpenSiteList(server)}
            >
              Site List
              <Chip
                label={`${selectedSites.length}/${allSites.length}`}
                size="small"
                sx={{
                  ml: 0.75, height: 18, fontSize: '0.65rem',
                  bgcolor: hasExclusions ? 'warning.light' : 'primary.light',
                  color:   hasExclusions ? 'warning.dark'  : 'primary.dark',
                }}
              />
            </Button>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onRemove} color="error">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {loadError && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {loadError}
        </Typography>
      )}
    </Box>
  );
};


// =============================================================================
// TrackerModal ← exportado
//
// Props:
//   open           → boolean
//   entry          → objecto da API (null = criar, objecto = editar)
//   saving         → boolean
//   onSave         → callback(formData)
//   onClose        → callback()
//   onDelete       → callback(id)
//   onOpenSiteList → callback(server, updateFn)
//                    O TrackerPage recebe o server e uma função que deve chamar
//                    com os selectedSites quando o utilizador confirmar.
// =============================================================================
const TrackerModal = ({ open, entry, saving, onSave, onClose, onDelete, onOpenSiteList }) => {
  const isEditing = Boolean(entry?.id);

  const [form,          setForm]          = useState(emptyForm());
  const [errors,        setErrors]        = useState({});
  const [technologies,  setTechnologies]  = useState([]);
  const [vendors,       setVendors]       = useState([]);
  const [features,      setFeatures]      = useState([]);
  const [teams,         setTeams]         = useState([]);
  const [owners,        setOwners]        = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);


  // ── Carregar lookups uma vez ao montar ──────────────────────────────────────
  useEffect(() => {
    Promise.all([apiGetTechnologies(), apiGetVendors(), apiGetFeatures(), apiGetTeams()])
      .then(([t, v, f, te]) => {
        setTechnologies(t.data.map((x) => x.name));
        setVendors(v.data.map((x) => x.name));
        setFeatures(f.data.map((x) => x.name));
        setTeams(te.data);
      })
      .catch(() => {});
  }, []);


  // ── Carregar owners quando o team muda ──────────────────────────────────────
  useEffect(() => {
    if (!form.teamId) { setOwners([]); return; }
    setLoadingOwners(true);
    apiGetOwnersByTeam(form.teamId)
      .then((res) => setOwners(res.data))
      .catch(() => setOwners([]))
      .finally(() => setLoadingOwners(false));
  }, [form.teamId]);


  // ── Popular o form quando o modal abre ──────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (!entry) { setForm(emptyForm()); return; }

    setForm({
      ...emptyForm(),
      ...entry,

      // Fix 1: normalizar datas para "yyyy-MM-dd" (API pode devolver "dd/MM/yyyy HH:mm:ss")
      startDate: toDateInput(entry.startDate),
      endDate:   toDateInput(entry.endDate),

      // Fix 2: IDs como strings — MUI Select compara por === e a API devolve números
      teamId:    entry.teamId != null ? String(entry.teamId) : '',
      // Suporta tanto ownerIds[] (novo) como ownerId (legado — entries antigas na BD)
      ownerIds:  entry.ownerIds?.length
        ? entry.ownerIds.map(String)
        : (entry.ownerId != null ? [String(entry.ownerId)] : []),
      ownerNames: entry.ownerNames?.length
        ? entry.ownerNames
        : (entry.ownerName ? [entry.ownerName] : []),

      servers: (entry.servers?.length ? entry.servers : []).map(normalizeServer),
      // Normaliza a data de cada phase (pode vir em vários formatos da API)
      phases:  entry.phases?.length
        ? entry.phases.map((p) => ({ ...p, date: toDateInput(p.date) }))
        : [],
    });
  }, [open, entry]);


  // ── Handlers de campos simples ───────────────────────────────────────────────
  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // Ao mudar a tecnologia, limpa os servers (dependem de technology+vendor)
  const handleTechnologyChange = (e) =>
    setForm((prev) => ({ ...prev, technology: e.target.value, servers: [] }));

  // Ao mudar o vendor, limpa igualmente os servers (dependem de technology+vendor)
  const handleVendorChange = (e) =>
    setForm((prev) => ({ ...prev, vendor: e.target.value, servers: [] }));

  // Team: actualiza nome e limpa owner (owner depende do team)
  const handleTeamChange = (e) => {
    const teamId   = e.target.value;
    const teamName = teams.find((t) => t.id === Number(teamId))?.name ?? '';
    setForm((prev) => ({ ...prev, teamId, teamName, ownerIds: [], ownerNames: [] }));
  };

  const handleOwnerChange = (e) => {
    // MUI Select multiple devolve sempre um array
    const ownerIds   = (Array.isArray(e.target.value) ? e.target.value : [e.target.value]).map(String);
    const ownerNames = ownerIds.map((id) => owners.find((o) => String(o.id) === id)?.name ?? '').filter(Boolean);
    setForm((prev) => ({ ...prev, ownerIds, ownerNames }));
  };


  // ── Handlers de Phases ───────────────────────────────────────────────────────
  const addPhase = () => {
    const n = form.phases.filter((p) => p.label !== 'Rollback').length + 1;
    setForm((prev) => ({ ...prev, phases: [...prev.phases, makePhase(`Phase ${n}`)] }));
  };

  const addRollback = () => {
    const n = form.phases.filter((p) => p.label?.startsWith('Rollback')).length + 1;
    const label = n === 1 ? 'Rollback' : `Rollback ${n}`;
    setForm((prev) => ({ ...prev, phases: [...prev.phases, makePhase(label)] }));
  };

  const updatePhase = (id, updated) =>
    setForm((prev) => ({ ...prev, phases: prev.phases.map((p) => p.id === id ? updated : p) }));

  const removePhase = (id) =>
    setForm((prev) => ({ ...prev, phases: prev.phases.filter((p) => p.id !== id) }));


  // ── Handlers de Servers ──────────────────────────────────────────────────────
  const addServer = () =>
    setForm((prev) => ({ ...prev, servers: [...prev.servers, makeServer()] }));

  const updateServer = (id, updated) =>
    setForm((prev) => ({ ...prev, servers: prev.servers.map((s) => s.id === id ? updated : s) }));

  const removeServer = (id) =>
    setForm((prev) => ({ ...prev, servers: prev.servers.filter((s) => s.id !== id) }));


  // ── Handler do Site List ─────────────────────────────────────────────────────
  // Passa ao TrackerPage o server E uma função de update com setForm funcional.
  // setForm funcional é crítico: lê sempre o state mais recente (evita closure stale).
  const handleOpenSiteList = (server) => {
    onOpenSiteList(
      server,
      (selectedSites) => {
        setForm((prev) => ({
          ...prev,
          servers: prev.servers.map((s) =>
            s.id === server.id ? { ...s, selectedSites } : s
          ),
        }));
      }
    );
  };


  // ── Save e Delete ────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.title?.trim())      e.title      = 'Title is required';
    if (!form.technology)         e.technology  = 'Technology is required';
    if (!form.vendor)             e.vendor      = 'Vendor is required';
    if (!form.feature)            e.feature     = 'Feature is required';
    if (!form.startDate)          e.startDate   = 'Start Date is required';
    if (!form.teamId)             e.teamId      = 'Team is required';
    if (!form.ownerIds?.length)   e.ownerIds    = 'Owner is required';
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setErrors({});
    onSave(isEditing ? { ...form, id: entry.id } : form);
  };

  const handleDelete = () => {
    if (window.confirm('Remove this activity? This cannot be undone.')) {
      onDelete(entry.id);
    }
  };



  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          {isEditing ? 'Edit Activity' : 'New Activity'}
        </Box>
        <IconButton onClick={onClose} size="small" disabled={saving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>

        {/* ── Identity ── */}
        <SectionLabel>Identity</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={12}>
            <TextField
              fullWidth label="Title *" name="title"
              value={form.title}
              onChange={(e) => { handleChange(e); setErrors((p) => ({ ...p, title: undefined })); }}
              placeholder="e.g. 5G Carrier Aggregation Feature Rollout"
              error={Boolean(errors.title)}
              helperText={errors.title}
            />
          </Grid>
        </Grid>

        {/* ── Classification ── */}
        <SectionLabel>Classification</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth select label="Technology *" name="technology"
              value={form.technology}
              onChange={(e) => { handleTechnologyChange(e); setErrors((p) => ({ ...p, technology: undefined })); }}
              error={Boolean(errors.technology)}
              helperText={errors.technology}
            >
              {technologies.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth select label="Vendor *" name="vendor"
              value={form.vendor}
              onChange={(e) => { handleVendorChange(e); setErrors((p) => ({ ...p, vendor: undefined })); }}
              error={Boolean(errors.vendor)}
              helperText={errors.vendor}
            >
              {vendors.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth select label="Feature *" name="feature"
              value={form.feature}
              onChange={(e) => { handleChange(e); setErrors((p) => ({ ...p, feature: undefined })); }}
              error={Boolean(errors.feature)}
              helperText={errors.feature}
            >
              {features.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>

        {/* ── Timeline ── */}
        <SectionLabel>Timeline</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth type="date" label="Start Date *" name="startDate"
              value={form.startDate ?? ''}
              onChange={(e) => { handleChange(e); setErrors((p) => ({ ...p, startDate: undefined })); }}
              slotProps={{ inputLabel: { shrink: true } }}
              error={Boolean(errors.startDate)}
              helperText={errors.startDate}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth type="date" label="End Date" name="endDate"
              value={form.endDate ?? ''}
              onChange={handleChange}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>

        {/* ── Servers ── */}
        <SectionLabel>Servers</SectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {form.servers.map((server) => (
            <ServerCard
              key={server.id}
              server={server}
              technology={form.technology}
              vendor={form.vendor}
              onChange={(updated) => updateServer(server.id, updated)}
              onRemove={() => removeServer(server.id)}
              onOpenSiteList={handleOpenSiteList}
            />
          ))}
          <Button
            variant="outlined" size="small" startIcon={<AddIcon />}
            onClick={addServer} sx={{ alignSelf: 'flex-start' }}
          >
            Add Server
          </Button>
        </Box>

        {/* ── Phases ── */}
        <SectionLabel>Phases</SectionLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {form.phases.map((ph) => (
            <PhaseCard
              key={ph.id}
              phase={ph}
              onChange={(updated) => updatePhase(ph.id, updated)}
              onRemove={() => removePhase(ph.id)}
            />
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addPhase} sx={{ flex: 2 }}>
              Add Phase
            </Button>
            <Button
              variant="outlined" size="small" color="error"
              startIcon={<AddIcon />} onClick={addRollback}
              sx={{ flex: 1 }}
            >
              Add Rollback
            </Button>
          </Box>
        </Box>

        {/* ── Ownership ── */}
        <SectionLabel>Ownership</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth select label="Team *"
              value={teams.length === 0 ? '' : (form.teamId ?? '')}
              onChange={(e) => { handleTeamChange(e); setErrors((p) => ({ ...p, teamId: undefined })); }}
              error={Boolean(errors.teamId)}
              helperText={errors.teamId}
            >
              <MenuItem value=""><em>— Select team —</em></MenuItem>
              {teams.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth select label="Activity Owner *"
              error={Boolean(errors.ownerIds)}
              helperText={errors.ownerIds}
              value={(owners.length === 0 || loadingOwners) ? [] : (form.ownerIds ?? [])}
              onChange={(e) => { handleOwnerChange(e); setErrors((p) => ({ ...p, ownerIds: undefined })); }}
              disabled={!form.teamId || loadingOwners}
              SelectProps={{
                multiple: true,
                renderValue: (selected) =>
                  selected.length === 0
                    ? <em>{form.teamId ? '— Select owner —' : 'Select a team first'}</em>
                    : selected
                        .map((id) => owners.find((o) => String(o.id) === id)?.name ?? id)
                        .join(', '),
              }}
              slotProps={{
                input: {
                  endAdornment: loadingOwners
                    ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                    : undefined,
                },
              }}
            >
              {owners.length === 0 && !loadingOwners && (
                <MenuItem value="" disabled>
                  <em>{form.teamId ? 'No owners available' : 'Select a team first'}</em>
                </MenuItem>
              )}
              {owners.length > 0 && (() => {
                const allSelected = owners.length > 0 && owners.every((o) => (form.ownerIds ?? []).includes(String(o.id)));
                const someSelected = !allSelected && (form.ownerIds ?? []).length > 0;
                return (
                  <ListSubheader
                    onMouseDown={(e) => {
                      e.preventDefault(); // evita fechar o dropdown
                      const allIds   = owners.map((o) => String(o.id));
                      const allNames = owners.map((o) => o.name);
                      setForm((prev) => ({
                        ...prev,
                        ownerIds:   allSelected ? [] : allIds,
                        ownerNames: allSelected ? [] : allNames,
                      }));
                      setErrors((p) => ({ ...p, ownerIds: undefined }));
                    }}
                    sx={{
                      cursor: 'pointer', display: 'flex', alignItems: 'center',
                      gap: 1, borderBottom: '1px solid', borderColor: 'divider',
                      pb: 0.5, userSelect: 'none',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Checkbox checked={allSelected} indeterminate={someSelected} size="small" sx={{ p: 0 }} />
                    <em style={{ fontStyle: 'italic', fontSize: '0.875rem' }}>
                      {allSelected ? 'Unselect All' : 'Select All'}
                    </em>
                  </ListSubheader>
                );
              })()}
              {owners.map((o) => (
                <MenuItem key={o.id} value={String(o.id)}>
                  <Checkbox checked={(form.ownerIds ?? []).includes(String(o.id))} size="small" sx={{ mr: 1, p: 0 }} />
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* ── Comments ── */}
        <SectionLabel>Comments</SectionLabel>
        <TextField
          fullWidth multiline rows={3}
          label="General Comments" name="comments"
          value={form.comments ?? ''}
          onChange={handleChange}
          placeholder="Observations, KPIs to monitor, dependencies…"
        />

      </DialogContent>
      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box>
          {isEditing && (
            <Button
              variant="outlined" color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleDelete}
              disabled={saving}
            >
              Delete Activity
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>          
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          {isEditing && (
            <Button
              variant="outlined" color="inherit"
              startIcon={<DownloadIcon />}
              onClick={() => apiExportById(entry.id, entry.title)}
              disabled={saving}
            >
              Export
            </Button>
          )}
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving
              ? <CircularProgress size={18} color="inherit" />
              : isEditing ? 'Save Changes' : 'Create Activity'
            }
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TrackerModal;